import { Card as HeroCard, Skeleton, Tooltip, cn } from '@heroui/react';
import React, { createContext, useContext, useRef } from 'react';

import isTextTruncated from '~/common/helpers/isTextTruncated';

type CardContextValue = {
  isLoaded?: boolean;
};

const CardContext = createContext<CardContextValue | undefined>(undefined);

const useCardContext = (componentName: string): CardContextValue => {
  const context = useContext(CardContext);
  if (context === undefined) {
    throw new Error(`${componentName} must be used within a Card component`);
  }
  return context;
};

type CardProps = {
  children: React.ReactNode;
  className?: string;
  isLoaded?: boolean;
  [key: string]: any;
};
const Card = ({
  children,
  className,
  isLoaded = true,
  ...props
}: CardProps) => {
  const contextValue = { isLoaded };

  return (
    <CardContext.Provider value={contextValue}>
      <HeroCard
        className={cn(
          'overflow-hidden rounded-lg border-1 border-solid border-outline',
          'transition-shadow duration-200 ease-in-out hover:shadow-small',
          className
        )}
        shadow='none'
        {...props}
      >
        {children}
      </HeroCard>
    </CardContext.Provider>
  );
};

type CardHeaderProps = {
  className?: string;
  icon: React.ReactNode;
  menuComponent?: React.ReactNode;
};
const CardHeader = ({ className, icon, menuComponent }: CardHeaderProps) => {
  const { isLoaded } = useCardContext('CardHeader');

  return (
    <div
      className={cn(
        'flex w-full items-center justify-between gap-1 p-4',
        className
      )}
    >
      <Skeleton isLoaded={isLoaded} className='rounded-md'>
        {icon}
      </Skeleton>
      {menuComponent && (
        <Skeleton isLoaded={isLoaded} className='rounded-md'>
          {menuComponent}
        </Skeleton>
      )}
    </div>
  );
};

type CardTitleProps = {
  children: React.ReactNode;
  className?: string;
};
const CardTitle = ({ children, className }: CardTitleProps) => {
  const { isLoaded } = useCardContext('CardTitle');
  const textRef = useRef<HTMLHeadingElement>(null);

  const tooltipContent = (
    <div className='max-w-xl whitespace-normal break-words w-max'>
      {children?.toString()}
    </div>
  );

  return (
    <Skeleton isLoaded={isLoaded} className='h-5 rounded-md'>
      <Tooltip
        content={tooltipContent}
        placement='top'
        isDisabled={
          !children ||
          !isTextTruncated(
            children.toString(),
            textRef as React.RefObject<HTMLParagraphElement>
          )
        }
      >
        <div className='inline-block w-full'>
          <h3
            ref={textRef}
            className={cn('truncate text-title-md text-on-surface', className)}
          >
            {children}
          </h3>
        </div>
      </Tooltip>
    </Skeleton>
  );
};

type CardDescriptionProps = {
  children: React.ReactNode;
  className?: string;
};
const CardDescription = ({ children, className }: CardDescriptionProps) => {
  const { isLoaded } = useCardContext('CardDescription');
  const textRef = useRef<HTMLParagraphElement>(null);

  const tooltipContent = (
    <div className='max-w-xl whitespace-normal break-words w-max'>
      {children?.toString()}
    </div>
  );

  return (
    <Skeleton isLoaded={isLoaded} className='h-5 rounded-md'>
      <Tooltip
        content={tooltipContent}
        placement='top'
        isDisabled={!children || !isTextTruncated(children.toString(), textRef)}
      >
        <div className='inline-block w-full'>
          <p
            ref={textRef}
            className={cn(
              'line-clamp-2 text-body-regular-md text-on-surface-variant',
              className
            )}
          >
            {children}
          </p>
        </div>
      </Tooltip>
    </Skeleton>
  );
};

type CardContentProps = {
  children?: React.ReactNode;
  className?: string;
  title?: string;
  titleEndContent?: React.ReactNode;
  description?: string;
};
const CardContent = ({
  children,
  className,
  title,
  titleEndContent,
  description
}: CardContentProps) => (
  // No gap between the title and the description: their line boxes already carry the rhythm the
  // design draws (24px over 20px).
  <div className={cn('flex min-h-20 flex-col px-4', className)}>
    {titleEndContent ? (
      <div className='flex min-w-0 items-center gap-1.5'>
        <Card.Title>{title}</Card.Title>
        {titleEndContent}
      </div>
    ) : (
      <Card.Title>{title}</Card.Title>
    )}
    <Card.Description>{description}</Card.Description>
    {children}
  </div>
);

type CardCounterProps = {
  count?: number;
  icon: React.ReactElement;
  tooltipContent?: string;
};
const CardCounter = (props: CardCounterProps) => {
  const { count, icon, tooltipContent } = props;
  const { isLoaded } = useCardContext('CardCounter');

  return (
    <Skeleton isLoaded={isLoaded} className='h-4 min-w-6 rounded-md'>
      <Tooltip
        content={tooltipContent}
        placement='top'
        isDisabled={!tooltipContent || count === undefined}
      >
        {count !== undefined && (
          <div className='flex min-w-6 items-center gap-1'>
            {React.cloneElement(icon, {
              className: 'w-4 h-4 text-on-surface-variant'
            })}
            <span className='font-normal text-sm leading-none tracking-tight text-on-surface-variant'>
              {count}
            </span>
          </div>
        )}
      </Tooltip>
    </Skeleton>
  );
};

type CardVersionProps = {
  versionMajor: number;
  versionMinor: number;
};
const CardVersion = (props: CardVersionProps) => {
  const { versionMajor, versionMinor } = props;
  const { isLoaded } = useCardContext('CardVersion');

  return (
    <Skeleton isLoaded={isLoaded} className='max-w-12 rounded-md'>
      <div className='flex h-5 items-center whitespace-nowrap rounded-md bg-surface-dim px-1 text-label-md text-on-surface'>
        {`v ${versionMajor}.${versionMinor}`}
      </div>
    </Skeleton>
  );
};

type CardFooterProps = {
  children: React.ReactNode;
  className?: string;
};
const CardFooter = ({ children, className }: CardFooterProps) => (
  <div className={cn('mt-2 flex items-center p-4', className)}>{children}</div>
);

Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Description = CardDescription;
Card.Content = CardContent;
Card.Counter = CardCounter;
Card.Version = CardVersion;
Card.Footer = CardFooter;

export default Card;
